import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, onAuthStateChanged, logout } from '../lib/firebase';
import { apiFetch } from '../lib/api';

const AuthContext = createContext(null);

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
function cacheKey(uid) { return `mig_ent_${uid}`; }

function readCache(uid) {
  try {
    const raw = localStorage.getItem(cacheKey(uid));
    if (!raw) return null;
    const { tier, cachedAt } = JSON.parse(raw);
    if (Date.now() - cachedAt > CACHE_TTL_MS) return null;
    return tier;
  } catch { return null; }
}

function writeCache(uid, tier) {
  try {
    localStorage.setItem(cacheKey(uid), JSON.stringify({ tier, cachedAt: Date.now() }));
  } catch {}
}

function clearCache(uid) {
  try { localStorage.removeItem(cacheKey(uid)); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(undefined); // undefined = loading
  const [entitlement, setEntitlement] = useState('free');
  const lastUid = useRef(null);

  async function fetchEntitlement(firebaseUser, force = false) {
    if (!force) {
      const cached = readCache(firebaseUser.uid);
      if (cached) { setEntitlement(cached); return; }
    }
    try {
      const token = await firebaseUser.getIdToken();
      const res   = await apiFetch('/api/billing/entitlement', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const tier = data.tier ?? 'free';
        setEntitlement(tier);
        writeCache(firebaseUser.uid, tier);
      }
    } catch {
      setEntitlement('free');
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser && lastUid.current) clearCache(lastUid.current);
      lastUid.current = firebaseUser?.uid ?? null;
      setUser(firebaseUser ?? null);
      if (firebaseUser) fetchEntitlement(firebaseUser);
      else setEntitlement('free');
    });
    return unsub;
  }, []);

  async function refreshEntitlement() {
    if (auth.currentUser) {
      clearCache(auth.currentUser.uid);
      await fetchEntitlement(auth.currentUser, true);
    }
  }

  const value = { user, entitlement, loading: user === undefined, logout, refreshEntitlement };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
